import React from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin } from "lucide-react";
import PageHead from "@/components/PageHead";

// Section + Bullets primitives mirror the shape used on /privacy so the two legal
// pages are visually and structurally consistent. Kept local — no shared surface.
const Section = ({ number, title, children }) => (
  <section
    className="border-t border-white/5 first:border-t-0"
    data-testid={`terms-section-${number}`}
  >
    <div className="max-w-4xl mx-auto px-6 md:px-10 py-12 md:py-14">
      <p className="label-mono text-terracotta">SECTION · {String(number).padStart(2, "0")}</p>
      <h2 className="font-serif text-2xl sm:text-3xl leading-tight text-bone mt-5">{title}</h2>
      <div className="mt-6 space-y-4 max-w-2xl text-base text-white/60 leading-relaxed">
        {children}
      </div>
    </div>
  </section>
);

const Bullets = ({ items }) => (
  <ul className="list-disc pl-6 space-y-1.5 text-base text-white/60 leading-relaxed marker:text-terracotta/70">
    {items.map((it) => (
      <li key={it}>{it}</li>
    ))}
  </ul>
);

const EmailLink = ({ testid }) => (
  <a
    href="mailto:info@chronovaults.co.uk"
    className="text-bone/90 hover:text-terracotta transition-colors underline decoration-terracotta/40 underline-offset-4"
    data-testid={testid}
  >
    info@chronovaults.co.uk
  </a>
);

export default function Terms() {
  return (
    <>
      <PageHead
        title="Terms & Conditions | ChronoVaults"
        description="The terms that govern your use of ChronoVaults — account, credits, membership, scheduled delivery, and acceptable use."
        canonical="https://chronovaults.co.uk/terms"
      />
    <div className="relative" data-testid="terms-page">
      {/* HERO */}
      <section className="relative overflow-hidden grain">
        <div className="max-w-4xl mx-auto px-6 md:px-10 pt-20 pb-16">
          <div className="animate-fade-in">
            <p className="label-mono text-terracotta" data-testid="terms-eyebrow">
              LEGAL · TERMS &amp; CONDITIONS
            </p>
            <h1
              className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-bone mt-8"
              data-testid="terms-heading"
            >
              Terms &amp; <em className="text-terracotta not-italic font-light">Conditions</em>
            </h1>
            <p
              className="label-mono text-white/50 mt-6"
              data-testid="terms-last-updated"
            >
              LAST UPDATED · 12 SEPTEMBER 2026
            </p>

            <div className="mt-8 space-y-5 max-w-2xl">
              <p className="text-lg text-white/70 leading-relaxed">
                These Terms &amp; Conditions govern your use of the ChronoVaults website and
                services.
              </p>
              <p className="text-base text-white/60 leading-relaxed">
                By creating an account, purchasing a service, creating a ChronoVault or otherwise
                using ChronoVaults, you agree to these Terms.
              </p>
              <p className="text-base text-white/60 leading-relaxed">Please read them carefully.</p>
              <p className="text-base text-white/60 leading-relaxed">
                ChronoVaults is based in the United Kingdom.
              </p>
              <p className="text-base text-white/60 leading-relaxed">
                For questions about these Terms, contact:{" "}
                <EmailLink testid="terms-hero-email" />
              </p>
            </div>
          </div>
        </div>
      </section>

      <Section number={1} title="About ChronoVaults">
        <p>
          ChronoVaults enables users to create personal messages and video content and schedule
          them for future delivery to an intended recipient.
        </p>
        <p>
          The service may include account creation, content storage, scheduled delivery,
          recipient notifications, identity or contact verification, payment processing and
          related functionality.
        </p>
        <p>ChronoVaults is intended for personal future messaging and memory preservation.</p>
        <p>
          It is not a legal, testamentary, probate, emergency, medical or financial service.
        </p>
      </Section>

      <Section number={2} title="Eligibility">
        <p>You must be at least 18 years old to create or operate a ChronoVaults account.</p>
        <p>By creating an account, you confirm that:</p>
        <Bullets
          items={[
            "you are legally capable of entering into a contract;",
            "the information you provide is accurate and current; and",
            "you will use ChronoVaults only for lawful purposes.",
          ]}
        />
        <p>
          You are responsible for keeping your login credentials secure and for activity carried
          out through your account.
        </p>
      </Section>

      <Section number={3} title="Creating a ChronoVault">
        <p>When creating a ChronoVault, you may provide content such as:</p>
        <Bullets
          items={[
            "written messages;",
            "video or other supported media;",
            "recipient contact information;",
            "delivery dates or instructions; and",
            "information required to facilitate recipient verification.",
          ]}
        />
        <p>
          You are responsible for checking that recipient details and delivery instructions are
          accurate before confirming a ChronoVault.
        </p>
        <p>
          ChronoVaults cannot guarantee successful delivery where information supplied by you is
          incorrect, incomplete or outdated.
        </p>
      </Section>

      <Section number={4} title="Recipient information">
        <p>
          You may provide another person's email address, telephone number or other information
          where reasonably necessary for us to deliver and secure a ChronoVault.
        </p>
        <p>
          You are responsible for ensuring that you have a lawful and legitimate reason to
          provide that information to ChronoVaults.
        </p>
        <p>
          Recipient information must not be used to harass, threaten, deceive or impersonate
          another person.
        </p>
        <p>Recipients may be required to complete verification steps before accessing a released ChronoVault.</p>
        <p>
          Successful notification does not necessarily mean that the recipient has accessed or
          viewed the content.
        </p>
      </Section>

      <Section number={5} title="Scheduled delivery">
        <p>
          ChronoVaults is designed to release messages according to the delivery instructions
          associated with a ChronoVault.
        </p>
        <p>
          We aim to deliver messages at or around the scheduled time, but exact delivery cannot
          be guaranteed.
        </p>
        <p>Delivery may be delayed or prevented by circumstances including:</p>
        <Bullets
          items={[
            "internet or telecommunications outages;",
            "unavailable third-party providers;",
            "invalid or changed recipient contact details;",
            "email filtering or spam controls;",
            "mobile network problems;",
            "recipient verification failures;",
            "scheduled maintenance;",
            "security measures; or",
            "circumstances outside our reasonable control.",
          ]}
        />
        <p>
          You should not use ChronoVaults where delivery at an exact time is safety-critical,
          legally required or otherwise essential.
        </p>
      </Section>

      <Section number={6} title="Not a will or legal-instruction service">
        <p>ChronoVaults must not be relied upon as:</p>
        <Bullets
          items={[
            "a will or codicil;",
            "a substitute for a legally executed will;",
            "an executor or probate service;",
            "a power of attorney;",
            "a legally binding instruction to relatives, financial institutions or authorities;",
            "an emergency notification system; or",
            "the sole location of information that must legally or urgently be available.",
          ]}
        />
        <p>
          If you need legally binding arrangements for what happens after death or incapacity,
          you should obtain appropriate professional advice.
        </p>
      </Section>

      <Section number={7} title="Pricing">
        <p>Current prices are displayed before purchase.</p>
        <p>At the date of these Terms, ChronoVaults offers:</p>
        <dl className="space-y-4">
          <div>
            <dt className="font-serif text-bone text-lg">Standard message</dt>
            <dd className="text-white/60 mt-1">£2.99 per message.</dd>
          </div>
          <div>
            <dt className="font-serif text-bone text-lg">ChronoVaults Membership</dt>
            <dd className="text-white/60 mt-1">£5 per month.</dd>
          </div>
          <div>
            <dt className="font-serif text-bone text-lg">Member message</dt>
            <dd className="text-white/60 mt-1">£1 per message while an eligible membership is active.</dd>
          </div>
        </dl>
        <p>
          The £5 monthly membership does not include a bundle of messages. It provides access to
          the applicable member message price while the membership remains active.
        </p>
        <p>Applicable prices and charges are shown before you confirm payment.</p>
        <p>
          We may change prices for future purchases. Any change affecting an existing
          subscription will be communicated as required by applicable law.
        </p>
      </Section>

      <Section number={8} title="Membership subscriptions">
        <p>ChronoVaults Membership is a recurring monthly subscription.</p>
        <p>
          Unless cancelled, the subscription will continue and the applicable subscription fee
          will be charged using the payment method associated with your Stripe account.
        </p>
        <p>You may cancel your membership using the available billing-management functionality.</p>
        <p>Cancellation does not affect purchases already completed before cancellation.</p>
        <p>The effect and timing of cancellation will be displayed through the billing service when you cancel.</p>
        <p>We will not impose unreasonable barriers to cancelling a membership.</p>
        <p>Your statutory consumer rights are not affected.</p>
      </Section>

      <Section number={9} title="Message purchases and credits">
        <p>
          A successful eligible message purchase adds the applicable message entitlement or
          credit to your ChronoVaults account.
        </p>
        <p>Message credits:</p>
        <Bullets
          items={[
            "are intended for use within ChronoVaults;",
            "have no cash value;",
            "cannot normally be transferred between users; and",
            "cannot be exchanged for cash except where required by law or where ChronoVaults expressly agrees otherwise.",
          ]}
        />
        <p>A message credit is consumed when used in accordance with the service's current message-creation process.</p>
        <p>
          Unless otherwise stated at the time of purchase, ChronoVaults will not impose an
          arbitrary expiry solely because a purchased message credit has remained unused.
        </p>
      </Section>

      <Section number={10} title="Payments">
        <p>Payments are processed by third-party payment provider Stripe.</p>
        <p>ChronoVaults does not store your complete payment-card details.</p>
        <p>
          You agree to provide accurate payment information and authorise the applicable charge
          when making a purchase.
        </p>
        <p>A purchase is not complete until payment has been successfully authorised and recorded.</p>
        <p>
          Where a payment is reversed, charged back, refunded or found to have been made
          fraudulently, we may reverse any corresponding unused credit or entitlement.
        </p>
      </Section>

      <Section number={11} title="Cancellations and refunds">
        <p>Nothing in these Terms limits rights that you have under applicable consumer law.</p>
        <p>Where you believe:</p>
        <Bullets
          items={[
            "you have been charged incorrectly;",
            "a duplicate payment has occurred;",
            "you paid for something that was not supplied;",
            "there has been an unauthorised transaction; or",
            "you are otherwise legally entitled to a refund,",
          ]}
        />
        <p>
          please contact <EmailLink testid="terms-refunds-email" />.
        </p>
        <p>Refund requests will be considered in accordance with applicable law and the circumstances of the transaction.</p>
        <p>
          We will not use these Terms to exclude refund or cancellation rights that cannot
          lawfully be excluded.
        </p>
      </Section>

      <Section number={12} title="Your content">
        <p>You retain ownership of content that you create or upload to ChronoVaults.</p>
        <p>
          You give ChronoVaults a limited permission to host, store, process, transmit and make
          that content available only as reasonably necessary to operate the service and fulfil
          your delivery instructions.
        </p>
        <p>This permission does not transfer ownership of your content to ChronoVaults.</p>
        <p>You confirm that you have the necessary rights to upload and use content submitted through your account.</p>
      </Section>

      <Section number={13} title="Prohibited content and behaviour">
        <p>You must not use ChronoVaults to create, store or deliver content that:</p>
        <Bullets
          items={[
            "is unlawful;",
            "threatens or deliberately harasses another person;",
            "facilitates fraud or impersonation;",
            "infringes copyright, privacy or other legal rights;",
            "contains malware or malicious code;",
            "promotes or facilitates serious criminal activity;",
            "exploits or endangers children;",
            "attempts to interfere with the operation or security of ChronoVaults; or",
            "otherwise uses the service in a way that could cause serious harm to ChronoVaults, its users or others.",
          ]}
        />
        <p>
          We may restrict, suspend or terminate access where reasonably necessary to address
          serious misuse, security threats or legal obligations.
        </p>
      </Section>

      <Section number={14} title="Content moderation and access">
        <p>ChronoVaults is designed around the privacy of personal messages.</p>
        <p>We do not claim a general right to casually inspect users' private content.</p>
        <p>
          However, content or account information may be accessed or processed where reasonably
          necessary to:
        </p>
        <Bullets
          items={[
            "provide technical support requested by the user;",
            "investigate security incidents or serious abuse;",
            "comply with applicable law or a lawful authority;",
            "protect the rights or safety of users or others; or",
            "maintain and operate the service.",
          ]}
        />
        <p>Any such access should be limited to what is reasonably necessary for the relevant purpose.</p>
      </Section>

      <Section number={15} title="Availability of the service">
        <p>
          We aim to keep ChronoVaults available and reliable, but we do not promise
          uninterrupted or error-free operation.
        </p>
        <p>
          The service may occasionally be unavailable because of maintenance, technical faults,
          third-party outages, security incidents or circumstances outside our reasonable
          control.
        </p>
        <p>Where practical, we will work to restore affected services as soon as reasonably possible.</p>
      </Section>

      <Section number={16} title="Third-party services">
        <p>ChronoVaults relies on third-party providers for functions such as:</p>
        <Bullets
          items={[
            "hosting and infrastructure;",
            "data storage;",
            "email delivery;",
            "SMS delivery;",
            "payment processing; and",
            "other supporting technology.",
          ]}
        />
        <p>
          We are not responsible for third-party services to the extent that failures are
          genuinely outside our reasonable control, but this does not exclude responsibilities
          that cannot lawfully be excluded.
        </p>
      </Section>

      <Section number={17} title="Account suspension or termination">
        <p>You may stop using ChronoVaults at any time.</p>
        <p>We may suspend or terminate an account where reasonably necessary because of:</p>
        <Bullets
          items={[
            "serious or repeated breach of these Terms;",
            "fraud or attempted fraud;",
            "misuse of the platform;",
            "threats to security;",
            "unlawful activity; or",
            "legal or regulatory requirements.",
          ]}
        />
        <p>
          Where appropriate, we will provide reasonable notice or an opportunity to remedy the
          issue before termination.
        </p>
        <p>
          We may act immediately where necessary to address fraud, serious abuse, security risks
          or legal obligations.
        </p>
      </Section>

      <Section number={18} title="Account closure and stored content">
        <p>If you close your account, access to associated functionality may end.</p>
        <p>
          Before closing an account containing scheduled ChronoVaults, you should consider
          whether you want to cancel, download or otherwise manage those messages where those
          options are available.
        </p>
        <p>
          Some information may remain temporarily in backups, transaction records, security logs
          or other records where required for technical, legal, fraud-prevention or regulatory
          reasons.
        </p>
        <p>
          Further information about personal-data retention is provided in our{" "}
          <Link
            to="/privacy"
            className="text-bone/90 hover:text-terracotta transition-colors underline decoration-terracotta/40 underline-offset-4"
            data-testid="terms-inline-privacy-link"
          >
            Privacy Policy
          </Link>
          .
        </p>
      </Section>

      <Section number={19} title="Intellectual property">
        <p>
          ChronoVaults and its associated website design, branding, software and original
          materials are protected by applicable intellectual-property laws.
        </p>
        <p>
          Except for your own content, you may not copy, reproduce, distribute, modify or
          commercially exploit ChronoVaults materials without permission unless permitted by law.
        </p>
      </Section>

      <Section number={20} title="Liability">
        <p>
          Nothing in these Terms excludes or limits liability where it would be unlawful to do
          so, including liability that cannot be excluded under applicable consumer law.
        </p>
        <p>
          Subject to those rights, ChronoVaults is not responsible for losses caused solely by
          circumstances outside our reasonable control.
        </p>
        <p>
          ChronoVaults is also not responsible for the personal consequences of the content of a
          message merely because that content was created or scheduled using the service.
        </p>
        <p>You remain responsible for deciding what you communicate and to whom.</p>
        <p>
          We do not exclude responsibility for providing the ChronoVaults service with
          reasonable care and skill where the law requires us to do so.
        </p>
      </Section>

      <Section number={21} title="Privacy">
        <p>
          Our use of personal information is described in our{" "}
          <Link
            to="/privacy"
            className="text-bone/90 hover:text-terracotta transition-colors underline decoration-terracotta/40 underline-offset-4"
            data-testid="terms-section-21-privacy-link"
          >
            Privacy Policy
          </Link>
          .
        </p>
        <p>The Privacy Policy forms an important part of understanding how ChronoVaults operates.</p>
      </Section>

      <Section number={22} title="Changes to the service or these Terms">
        <p>
          We may update ChronoVaults and these Terms as the service develops or where changes
          are required for legal, security or operational reasons.
        </p>
        <p>
          Where a change materially affects existing users or their rights, we will provide
          reasonable notice where required.
        </p>
        <p>
          The latest version of these Terms will be available on the ChronoVaults website and
          will display its last-updated date.
        </p>
      </Section>

      <Section number={23} title="Governing law">
        <p>
          These Terms are governed by the laws applicable in England and Wales, subject to any
          mandatory consumer protections that apply to you in the part of the United Kingdom or
          other jurisdiction where you live.
        </p>
        <p>
          Nothing in these Terms prevents a consumer from relying on rights or protections that
          applicable law says cannot be excluded.
        </p>
      </Section>

      <Section number={24} title="Contact us">
        <p>Questions about these Terms can be sent to:</p>
        <address className="not-italic pl-6 border-l-2 border-terracotta/40 py-1 my-2 space-y-1">
          <div className="font-serif text-lg text-bone">ChronoVaults</div>
          <div className="text-white/70">United Kingdom</div>
          <div className="text-white/70">
            <EmailLink testid="terms-contact-email" />
          </div>
        </address>
      </Section>

      {/* CONTACT / LEGAL FOOTER (matches About and Privacy footers) */}
      <footer
        className="border-t border-white/10 bg-vault-950"
        data-testid="terms-footer"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="space-y-3">
              <div
                className="flex items-center gap-2 text-sm text-white/60"
                data-testid="terms-footer-location"
              >
                <MapPin className="w-4 h-4 text-terracotta" strokeWidth={1.5} />
                <span>ChronoVaults is based in the United Kingdom</span>
              </div>
              <a
                href="mailto:info@chronovaults.co.uk"
                className="flex items-center gap-2 text-sm text-bone/80 hover:text-terracotta transition-colors"
                data-testid="terms-footer-email"
              >
                <Mail className="w-4 h-4 text-terracotta" strokeWidth={1.5} />
                <span>info@chronovaults.co.uk</span>
              </a>
            </div>

            <nav
              className="flex items-center gap-6 text-xs label-mono"
              data-testid="terms-footer-legal"
              aria-label="Legal"
            >
              <Link
                to="/about"
                className="text-white/60 hover:text-bone transition-colors"
                data-testid="terms-footer-about"
              >
                About
              </Link>
              <span className="text-white/20">·</span>
              <Link
                to="/privacy"
                className="text-white/60 hover:text-bone transition-colors"
                data-testid="terms-footer-privacy"
              >
                Privacy Policy
              </Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
}
